import {
  ActionIcon,
  Blockquote,
  Box,
  Button,
  Flex,
  Group,
  List,
  Modal,
  Paper,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { type TransformedValues, useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { ReviewModal } from "@/components/ReviewModal";
import { IconChevronRight } from "@tabler/icons-react";
import * as df from "date-fns";
import { useState } from "react";
import { useLocation, useParams } from "wouter";
import { client } from "@/api-client";
import type { FileOutput, SessionWithTrainerOutput } from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { Header } from "@/components/Header";
import { LibraryItem } from "@/components/LibraryItem";
import { MultilineText } from "@/components/MultilineText";
import { Portrait } from "@/components/Portrait";
import { SessionTab, SessionTabs } from "@/components/SessionTabs";
import {
  isSessionCancelled,
  renderCancelStatus,
} from "@/components/SessionUtils";
import { SurveyList } from "@/components/SurveyList";
import { OwnerRoutes, UniversalRoutes, useNavigator } from "@/routers/routes";

type SessionWithTrainer = SessionWithTrainerOutput;
// FileOutput is now imported directly from @/api-types

const SessionFiles = (props: {
  session: SessionWithTrainer;
  files: FileOutput[];
}) => {
  const { session, files } = props;

  if (!session.fileIds || session.fileIds.length === 0) {
    return null;
  }

  return (
    <Stack w="100%">
      <Title order={5}>Files shared by the trainer</Title>
      {files
        .filter((file) => file.type !== "SURVEY")
        .map((file: FileOutput) => (
          <LibraryItem key={file.id} item={file} kind="file" readonly={true} />
        ))}
    </Stack>
  );
};

const SessionNotes = (props: { session: SessionWithTrainer }) => {
  const { session } = props;
  const [showOnboardingForm, setShowOnboardingForm] = useState(false);
  const [purpose, setPurpose] = useState(session.purpose);

  const futureSession =
    session.status !== "COMPLETED" && !isSessionCancelled(session.status);

  const purposeForm = useForm({
    initialValues: {
      purpose: purpose ?? "",
    },
    transformValues: (values) => ({
      purpose: values.purpose,
    }),
  });

  const saveSessionPurposeMutation = client.useMutation(
    "post",
    "/owner/sessions/{sessionId}/",
  );

  const onSaveSessionPurpose = async (
    values: TransformedValues<typeof purposeForm>,
  ) => {
    setShowOnboardingForm(false);
    await saveSessionPurposeMutation.mutateAsync({
      params: {
        path: {
          sessionId: session.id,
        },
      },
      body: {
        purpose: values.purpose,
      },
    });
    purposeForm.resetDirty();
    setPurpose(values.purpose);
  };

  return (
    <Stack w="100%">
      {!showOnboardingForm && purpose && (
        <Stack>
          <Blockquote p="xs">
            <Title order={5}>What do you need this session for?</Title>
            <MultilineText text={purpose} />
            <Text
              size="sm"
              c="dimmed"
              td="underline"
              onClick={() => setShowOnboardingForm(true)}
            >
              Edit
            </Text>
          </Blockquote>
        </Stack>
      )}
      {showOnboardingForm && (
        <form onSubmit={purposeForm.onSubmit(onSaveSessionPurpose)}>
          <Stack>
            <Textarea
              label="What do you need this session for?"
              {...purposeForm.getInputProps("purpose")}
              autosize
              minRows={4}
              placeholder={`Please tell ${session.trainer.firstName} what do you expect or want to achieve from this session.`}
            />
            <Button
              type="submit"
              fullWidth
              loading={saveSessionPurposeMutation.isPending}
            >
              Save
            </Button>
          </Stack>
        </form>
      )}
      {futureSession && !showOnboardingForm && !purpose && (
        <Paper
          p="md"
          withBorder
          mb="sm"
          bg="blue.0"
          onClick={() => setShowOnboardingForm(true)}
        >
          <Flex gap="sm" align="center">
            <Box style={{ flex: "1 1 auto" }}>
              <Text>
                Click to provide some information to help the trainer to
                prepare.
              </Text>
            </Box>
            <Box>
              <ActionIcon variant="transparent" size="lg" color="gray">
                <IconChevronRight />
              </ActionIcon>
            </Box>
          </Flex>
        </Paper>
      )}
      {session.preparation && (
        <Blockquote p="xs">
          <Title order={5}>Preparation instructions from the trainer</Title>
          <MultilineText text={session.preparation} />
        </Blockquote>
      )}
    </Stack>
  );
};

const RequestedSession = (props: {
  session: SessionWithTrainer;
  children?: React.ReactNode;
}) => {
  const { session, children } = props;
  const { navigate } = useNavigator();
  const [
    openedConfirmationModal,
    { open: openConfirmationModal, close: closeConfirmationModal },
  ] = useDisclosure(false);
  const [cancelReason, setCancelReason] = useState("");

  const cancelMutation = client.useMutation(
    "post",
    "/owner/sessions/{sessionId}/cancel",
  );

  const onCancel = async () => {
    await cancelMutation.mutateAsync({
      params: {
        path: {
          sessionId: session.id,
        },
      },
      body: {
        reason: cancelReason,
      },
    });
    closeConfirmationModal();
    navigate({ path: UniversalRoutes.HOME });
  };

  return (
    <>
      <Text>You proposed times:</Text>
      <List>
        {session.proposedTimes?.map((time) => (
          <List.Item key={time}>
            {df.format(df.parseISO(time), "MMM d, yyyy - hh:mm")}
          </List.Item>
        ))}
      </List>
      <Text>Waiting for {session.trainer.firstName} to confirm.</Text>
      <Text>We will notify you straight away!</Text>
      {children}
      <Button color="red.8" onClick={openConfirmationModal}>
        Cancel Session
      </Button>

      <Modal
        opened={openedConfirmationModal}
        onClose={closeConfirmationModal}
        title="Cancel Session Request"
      >
        <Stack>
          <TextInput
            label="Reason for cancellation (optional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.currentTarget.value)}
            placeholder="Please provide a reason"
          />
          <Group justify="flex-end">
            <Button variant="outline" onClick={closeConfirmationModal}>
              Never mind
            </Button>
            <Button
              color="red.8"
              onClick={onCancel}
              loading={cancelMutation.isPending}
            >
              Cancel Request
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

const ConfirmedSession = (props: {
  session: SessionWithTrainer;
  children?: React.ReactNode;
}) => {
  const { session, children } = props;
  const { navigate } = useNavigator();
  const [
    openedConfirmationModal,
    { open: openConfirmationModal, close: closeConfirmationModal },
  ] = useDisclosure(false);
  const [cancelReason, setCancelReason] = useState("");
  const sendMessage = client.useMutation(
    "post",
    "/universal/chats/{engagementId}/messages",
  );

  const onCancel = async () => {
    await sendMessage.mutateAsync({
      params: {
        path: {
          engagementId: session.engagementId,
        },
      },
      body: {
        message: cancelReason,
        specialType: "CANCEL_SESSION",
        objectId: session.id,
      },
    });
    closeConfirmationModal();
    navigate({
      path: UniversalRoutes.CHAT.DETAIL,
      params: { engagementId: session.engagementId },
    });
  };

  return (
    <>
      <Text>
        {"Scheduled for "}
        {session.scheduledAt &&
          df.format(df.parseISO(session.scheduledAt), "MMM d, yyyy h:mm a")}
      </Text>
      {children}
      <Button color="red.8" onClick={openConfirmationModal} mt="sm">
        Message {session.trainer.firstName} to cancel
      </Button>

      <Modal
        opened={openedConfirmationModal}
        onClose={closeConfirmationModal}
        title="Ask to cancel a session"
      >
        <Stack>
          <TextInput
            label="Message to trainer"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.currentTarget.value)}
            placeholder="Please explain why you would like to cancel"
          />
          <Group justify="flex-end">
            <Button variant="outline" onClick={closeConfirmationModal}>
              Never mind
            </Button>
            <Button
              color="red.8"
              onClick={onCancel}
              loading={sendMessage.isPending}
            >
              Send Cancellation
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

const CompletedSession = (props: {
  session: SessionWithTrainer;
  children?: React.ReactNode;
}) => {
  const { session, children } = props;
  const [reviewOpened, { open: openReview, close: closeReview }] =
    useDisclosure(false);
  const [reviewed, setReviewed] = useState(false);

  const { data: existingReview } = client.useQuery(
    "get",
    "/owner/trainers/{trainerId}/reviews/mine",
    { params: { path: { trainerId: session.trainer.id } } },
  );

  const hasReview = reviewed || !!existingReview;

  return (
    <>
      <Text>
        Session completed on{" "}
        {df.format(df.parseISO(session.lastUpdatedAt), "MMM d, yyyy h:mm a")}
      </Text>

      {!hasReview && (
        <Paper p="md" withBorder bg="green.0" onClick={openReview}>
          <Flex gap="sm" align="center">
            <Box style={{ flex: "1 1 auto" }}>
              <Text fw={500}>How was your session with {session.trainer.firstName}?</Text>
              <Text size="sm" c="dimmed">
                Leave a review to help other dog owners find the right trainer.
              </Text>
            </Box>
            <Box>
              <ActionIcon variant="transparent" size="lg" color="green">
                <IconChevronRight />
              </ActionIcon>
            </Box>
          </Flex>
        </Paper>
      )}

      {hasReview && (
        <Text size="sm" c="dimmed">
          ✓ You've reviewed {session.trainer.firstName}
        </Text>
      )}

      <ReviewModal
        opened={reviewOpened}
        onClose={closeReview}
        trainerId={session.trainer.id}
        trainerFirstName={session.trainer.firstName}
        onSuccess={() => setReviewed(true)}
      />

      {children}
    </>
  );
};

const CancelledSession = (props: {
  session: SessionWithTrainer;
  children?: React.ReactNode;
}) => {
  const { session, children } = props;
  return (
    <>
      {session.scheduledAt && (
        <Text>
          {"Original scheduled for "}
          {df.format(df.parseISO(session.scheduledAt), "MMM d, yyyy h:mm a")}
        </Text>
      )}
      {session.createdAt && (
        <Text>
          {"Requested on "}
          {df.format(df.parseISO(session.createdAt), "MMM d, yyyy h:mm a")}
        </Text>
      )}
      {renderCancelStatus(session.status, "owner")}
      {session.reason && (
        <Blockquote>
          <Text>{session.reason}</Text>
        </Blockquote>
      )}
      {children}
    </>
  );
};

export const Session = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { navigator, navigate } = useNavigator();
  const [location, setLocation] = useLocation();

  // Get initial tab from URL hash or default to PREPARATION
  const getInitialTab = (): SessionTab => {
    const hash = window.location.hash.slice(1).toLowerCase();
    return Object.values(SessionTab).includes(hash as SessionTab)
      ? (hash as SessionTab)
      : SessionTab.PREPARATION;
  };

  const [activeTab, setActiveTab] = useState<SessionTab>(getInitialTab);

  // Update URL hash when tab changes
  const handleTabChange = (tab: SessionTab) => {
    setActiveTab(tab);
    setLocation(`${location.split("#")[0]}#${tab}`);
  };

  const { data: session } = client.useQuery(
    "get",
    "/owner/sessions/{sessionId}",
    {
      params: {
        path: {
          sessionId,
        },
      },
    },
  );

  // Load files for both SessionFiles and SurveyList
  const { data: filesData } = client.useQuery(
    "get",
    "/universal/files-batch/batch-get",
    {
      params: {
        query: {
          fileIds: session?.fileIds ?? [],
        },
      },
    },
    {
      enabled: !!session?.fileIds && session.fileIds.length > 0,
    },
  );

  const hasFiles = session?.fileIds && session.fileIds.length > 0;

  // Get survey statuses for this session's files
  const { data: engagement } = client.useQuery(
    "get",
    "/owner/engagements/{engagementId}",
    {
      params: {
        path: {
          engagementId: session?.engagementId || "",
        },
      },
    },
    { enabled: !!session?.engagementId },
  );

  if (!(session && engagement && (filesData || !hasFiles))) {
    return <FancyLoader />;
  }

  const handleSurveyClick = (surveyId: string) => {
    navigate({
      path: `/engagements/${session.engagementId}/surveys/${surveyId}`,
    });
  };

  const allFiles = filesData?.files || [];

  return (
    <Stack>
      <Header title="View Session" />

      <Flex gap="sm" align="center">
        <Portrait
          profile={session.trainer}
          onClick={navigator({
            path: OwnerRoutes.TRAINER_ENGAGEMENT,
            params: { engagementId: session.engagementId },
          })}
          size="lg"
          who="person"
        />
        <Box
          style={{ flex: "1 1 auto" }}
          onClick={navigator({
            path: UniversalRoutes.TRAINERS.DETAIL,
            params: { trainerId: session.trainer.id },
          })}
        >
          <Text fw="bold">{session.sessionType.name}</Text>
          <Text>
            with {session.trainer.firstName} {session.trainer.lastName}
          </Text>
        </Box>
      </Flex>

      {session.status === "REQUESTED" && (
        <RequestedSession session={session}>
          <SessionTabs activeTab={activeTab} onTabChange={handleTabChange}>
            {activeTab === SessionTab.PREPARATION && (
              <SessionNotes session={session} />
            )}
            {activeTab === SessionTab.MATERIALS && (
              <SessionFiles session={session} files={allFiles} />
            )}
            {activeTab === SessionTab.SURVEYS && (
              <SurveyList
                files={allFiles}
                surveyStatuses={engagement.surveys || {}}
                onSurveyClick={handleSurveyClick}
              />
            )}
          </SessionTabs>
        </RequestedSession>
      )}

      {session.status === "CONFIRMED" && (
        <ConfirmedSession session={session}>
          <SessionTabs activeTab={activeTab} onTabChange={handleTabChange}>
            {activeTab === SessionTab.PREPARATION && (
              <SessionNotes session={session} />
            )}
            {activeTab === SessionTab.MATERIALS && (
              <SessionFiles session={session} files={allFiles} />
            )}
            {activeTab === SessionTab.SURVEYS && (
              <SurveyList
                files={allFiles}
                surveyStatuses={engagement.surveys}
                onSurveyClick={handleSurveyClick}
              />
            )}
          </SessionTabs>
        </ConfirmedSession>
      )}

      {session.status === "COMPLETED" && (
        <CompletedSession session={session}>
          <SessionTabs activeTab={activeTab} onTabChange={handleTabChange}>
            {activeTab === SessionTab.PREPARATION && (
              <SessionNotes session={session} />
            )}
            {activeTab === SessionTab.MATERIALS && (
              <SessionFiles session={session} files={allFiles} />
            )}
            {activeTab === SessionTab.SURVEYS && (
              <SurveyList
                files={allFiles}
                surveyStatuses={engagement.surveys}
                onSurveyClick={handleSurveyClick}
              />
            )}
          </SessionTabs>
        </CompletedSession>
      )}

      {isSessionCancelled(session.status) && (
        <CancelledSession session={session}>
          <SessionTabs activeTab={activeTab} onTabChange={handleTabChange}>
            {activeTab === SessionTab.PREPARATION && (
              <SessionNotes session={session} />
            )}
            {activeTab === SessionTab.MATERIALS && (
              <SessionFiles session={session} files={allFiles} />
            )}
            {activeTab === SessionTab.SURVEYS && (
              <SurveyList
                files={allFiles}
                surveyStatuses={engagement.surveys}
                onSurveyClick={handleSurveyClick}
              />
            )}
          </SessionTabs>
        </CancelledSession>
      )}
    </Stack>
  );
};
